import Preloader from '@components/preloader';
import cn from 'clsx';
import { observer } from 'mobx-react-lite';
import { ReactNode, useState } from 'react';

import { Pagination } from 'components/Pagination';
import { useStore } from 'hooks/useStore';
import { TSubscribedFilter } from 'store/Subscriptions/SubscribedFilter';
import type { SavedFilter } from 'store/TabsDataStore/TabsDataStore';

import { Card } from './Card';
import { Header } from './Header';
import { NoContent } from './NoContent';

import css from './TabsContent.module.css';

interface ITabsContent<T> {
  itemsPerColumn?: number;
  columns?: number;
  header: ReactNode;
  noContentElement?: ReactNode;
  items: T[];
  onItemClick: (item: T) => void;
  onItemDelete?: (item: T) => void;
  onItemSubscribe?: (item: T) => void;
  isLoading: boolean;
  activeItem: SavedFilter | null;
  itemsCounter?: 'filter' | 'truck';
  className?: string;
}

const TabsContent = <T extends TSubscribedFilter>({
  itemsPerColumn = 5,
  columns = 3,
  header = null,
  items = [],
  onItemClick = () => {},
  onItemDelete,
  onItemSubscribe,
  isLoading = false,
  activeItem,
  noContentElement = null,
  itemsCounter,
  className,
}: ITabsContent<T>) => {
  const {
    app: { locale },
    view,
  } = useStore();
  const [activePage, setActivePage] = useState(1);

  const pageCount = Math.ceil(items.length / (columns * itemsPerColumn));
  const pageBorder = (activePage - 1) * columns;

  if (activePage > 1 && activePage > pageCount) setActivePage(pageCount);

  const content = [];

  for (let i = 0; i < columns; i++) {
    const filtersArr = items.slice((i + pageBorder) * itemsPerColumn, (i + 1 + pageBorder) * itemsPerColumn);

    content.push(
      <div key={`column-${i}`} className={css.column}>
        {filtersArr.map(item => {
          const isActive = item.id === activeItem?.id;

          return (
            <Card<T>
              key={item.id}
              isActive={isActive}
              item={item}
              onItemClick={onItemClick}
              onItemDelete={onItemDelete}
              onItemSubscribe={onItemSubscribe}
            />
          );
        })}
      </div>,
    );
  }

  return (
    <div className={cn(css.container, { [css.container_centered]: !items.length }, className)}>
      {isLoading && (
        <div className={css.preloader}>
          <Preloader width={64} />
        </div>
      )}

      {!items.length && !isLoading && noContentElement}

      {Boolean(items.length) && (
        <>
          <div>{header}</div>

          <div className={css.content}>{content}</div>

          <div className={css.bottomContainer}>
            {itemsCounter && <span className={css.itemsCount}>{view.pluralizedCount(items.length, itemsCounter)}</span>}

            {pageCount > 1 && (
              <Pagination
                className={css.pagination}
                initialPage={activePage}
                locale={locale}
                pageCount={pageCount}
                onPageChange={({ selected }: { selected: number }) => setActivePage(selected)}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

TabsContent.Header = Header;
TabsContent.NoContent = NoContent;

const connected = observer(TabsContent);

export { connected as TabsContent };
